import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { RiskBadge } from "@/components/RiskBadge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { reducedPsychometricQuestions, calculatePsychometricProfile } from "@/data/reducedPsychometricQuestions";
import { Sparkles, Target, Trophy, Clock, Coins, ArrowRight, Shield } from "lucide-react";
import { RiskLevel } from "@/types/sorin";

const Onboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [processingResults, setProcessingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [riskScore, setRiskScore] = useState<number | null>(null);

  const totalQuestions = reducedPsychometricQuestions.length;
  const currentQuestion = reducedPsychometricQuestions[currentQuestionIndex];
  
  const getSectionInfo = () => {
    const section = currentQuestion.section;
    if (section === 'upps_p') {
      return {
        icon: Target,
        title: 'Tentang Dirimu',
        subtitle: 'Pahami cara kerjamu',
        color: 'primary'
      };
    }
    if (section === 'dospert') {
      return {
        icon: Trophy,
        title: 'Keputusan Keselamatan',
        subtitle: 'Bagaimana kamu memilih?',
        color: 'success'
      };
    }
    return {
      icon: Coins,
      title: 'Pilihan Masa Depan',
      subtitle: 'Sekarang atau nanti?',
      color: 'warning'
    };
  };

  const sectionInfo = getSectionInfo();

  const handleAnswer = async (score: number) => {
    const newAnswers = { ...answers, [currentQuestion.id]: score };
    setAnswers(newAnswers);

    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setProcessingResults(true);

      try {
        const profile = calculatePsychometricProfile(newAnswers);

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          toast({ title: "Error", description: "Authentication error", variant: "destructive" });
          return;
        }

        const { error: updateError } = await supabase
          .from('worker_profiles')
          .update({ psychometric_profile: profile })
          .eq('user_id', user.id);

        if (updateError) throw updateError;

        const { data: calcData, error: calcError } = await supabase.functions.invoke(
          'calculate-psychometric-profile',
          { body: { workerId: user.id, psychometricProfile: profile } }
        );

        if (calcError) throw calcError;

        setRiskScore(calcData.riskScore);
        setShowResults(true);
      } catch (error) {
        console.error("Error processing quiz:", error);
        toast({ title: "Error", description: "Failed to save quiz results", variant: "destructive" });
      } finally {
        setProcessingResults(false);
      }
    }
  };

  const getRiskLevel = (): RiskLevel => {
    if (!riskScore) return "Rendah";
    if (riskScore < 33) return "Rendah";
    if (riskScore < 67) return "Sedang";
    return "Tinggi";
  };

  return (
    <div className="min-h-screen pt-safe bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 5, repeat: Infinity }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-success/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 5, repeat: Infinity, delay: 1 }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl relative z-10"
      >
        {!processingResults && !showResults && (
          <>
            {/* Header */}
            <motion.div
              key={`section-${currentQuestion.section}`}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 text-center"
            >
              <motion.div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-3"
                whileHover={{ scale: 1.05 }}
              >
                <sectionInfo.icon className="h-5 w-5 text-primary" />
                <span className="font-semibold text-primary">{sectionInfo.title}</span>
              </motion.div>
              <p className="text-muted-foreground">{sectionInfo.subtitle}</p>
            </motion.div>

            {/* Progress */}
            <div className="glass-card rounded-2xl p-5 mb-4 border border-border/50">
              <div className="flex items-center justify-between text-sm mb-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="font-medium">Pertanyaan {currentQuestionIndex + 1} dari {totalQuestions}</span>
                </div>
                <span className="text-muted-foreground font-medium">{Math.round((currentQuestionIndex / totalQuestions) * 100)}%</span>
              </div>
              <Progress value={(currentQuestionIndex / totalQuestions) * 100} className="h-2" />
              <motion.div
                className="flex items-center gap-2 pt-3"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Sparkles className="h-4 w-4 text-warning" />
                <span className="text-xs text-muted-foreground">Profil keselamatanmu sedang dibentuk...</span>
              </motion.div>
            </div>

            {/* Question */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestionIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                <div className="glass-card rounded-3xl p-8 shadow-medium border border-border/50">
                  <motion.div
                    className="text-xl md:text-2xl font-bold leading-relaxed mb-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    {currentQuestion.question}
                  </motion.div>

                  {currentQuestion.section === 'delay_discounting' && (
                    <p className="text-sm text-muted-foreground mb-6">💡 Tidak ada jawaban benar atau salah</p>
                  )}

                  <div className="space-y-3">
                    {currentQuestion.section === 'delay_discounting' ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {currentQuestion.options.map((option, index) => (
                          <motion.button
                            key={option.value}
                            onClick={() => handleAnswer(option.value)}
                            className={`relative overflow-hidden rounded-2xl border-2 p-6 text-left transition-all hover:shadow-lg ${
                              index === 0 
                                ? 'border-success/50 bg-success/5 hover:bg-success/10 hover:border-success' 
                                : 'border-primary/50 bg-primary/5 hover:bg-primary/10 hover:border-primary'
                            }`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <div className="space-y-1">
                              <div className={`text-sm font-medium ${index === 0 ? 'text-success' : 'text-primary'}`}>
                                {index === 0 ? '🏃 Sekarang' : '⏰ Nanti'}
                              </div>
                              <div className="text-xl font-bold">{option.label}</div>
                            </div>
                          </motion.button>
                        ))}
                      </div>
                    ) : (
                      currentQuestion.options.map((option) => (
                        <motion.button
                          key={option.value}
                          onClick={() => handleAnswer(option.value)}
                          className="w-full p-4 rounded-xl border-2 border-border/50 hover:border-primary hover:bg-primary/5 transition-all text-left group"
                          whileHover={{ scale: 1.01, x: 4 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium group-hover:text-primary transition-colors">
                              {option.label}
                            </span>
                            <div className="w-5 h-5 rounded-full border-2 border-border/50 group-hover:border-primary group-hover:bg-primary/10 transition-all" />
                          </div>
                        </motion.button>
                      ))
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </>
        )}

        {processingResults && (
          <div className="glass-card rounded-3xl p-12 text-center space-y-6 border border-border/50">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-primary to-success flex items-center justify-center"
            >
              <Sparkles className="h-8 w-8 text-primary-foreground" />
            </motion.div>
            <div className="space-y-2">
              <h3 className="text-2xl font-bold">Menganalisis Profil...</h3>
              <p className="text-muted-foreground">Tunggu sebentar</p>
            </div>
          </div>
        )}

        {showResults && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
          >
            <div className="glass-card rounded-3xl p-12 text-center space-y-8 border border-border/50">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2 }}
              >
                <div className="mx-auto w-24 h-24 rounded-full bg-gradient-to-br from-primary to-success flex items-center justify-center mb-4 shadow-lg shadow-primary/25">
                  <Shield className="h-12 w-12 text-primary-foreground" />
                </div>
                <h2 className="text-3xl font-bold mb-2">Profil Keselamatanmu Siap!</h2>
                <p className="text-muted-foreground">Level risiko kamu saat ini</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex justify-center"
              >
                <RiskBadge level={getRiskLevel()} showLabel />
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <p className="text-muted-foreground max-w-md mx-auto mb-6">
                  Profil ini membantu SORIN memberikan saran keselamatan yang sesuai dengan karakteristikmu
                </p>
                <Button 
                  onClick={() => navigate("/home")} 
                  size="lg" 
                  className="w-full max-w-xs h-12 rounded-xl bg-gradient-to-r from-primary to-primary/80 hover:opacity-90"
                >
                  Mulai Laporkan Hazard
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default Onboarding;